import React, { useEffect, useState } from 'react';
import { router } from '@inertiajs/react';
import {
    Box,
    Typography,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import CustomCard from './CustomCard';
import { formatUtilityTypes } from '../utils/formatUtilityType';
import axiosClient from '../services/axiosClient';
import type { UtilBillPaymentStatus } from '../pages/util/UtilBills';

const FOREST_GREEN = '#228B22';
const EXPECTED_GREY = '#616161';
const WAIVED_GREY = '#9e9e9e';
const PENDING_REVIEW_AMBER = '#8a5a00';

export interface CurrentCycleShare {
    /**
     * NOTE: not in the implementation plan's literal §3.3 shape (which lists
     * only roommate_id/roommate_name/roommate_cost/status), but the paid/unpaid
     * toggle this card reuses from BillsCard (PATCH .../bill-shares/status)
     * addresses a specific UtilBillShare by id, and roommate_id alone can't
     * express that when a roommate could in principle have multiple shares.
     * Filling this gap by assuming the backend includes it.
     */
    id: string;
    roommate_id: string;
    roommate_name: string;
    roommate_cost: number;
    status: UtilBillPaymentStatus;
}

export interface CurrentCycleUtilBill {
    id: string;
    total_cost: number;
    due_date: string;
    review_status: 'pending_review' | 'approved' | 'auto_approved';
    voided_at: string | null;
    utility_types: string[];
    util_email_id: string | null;
}

export type CurrentCycleBillStatus = 'expected' | 'waived' | 'pending_review' | 'approved' | 'auto_approved';

export interface CurrentCycleBillRow {
    billing_cycle_bill_id: string;
    expected_bill_id: string;
    vendor_name: string;
    utility_types: string[];
    due_day: number;
    alert_threshold_date: string;
    status: CurrentCycleBillStatus;
    cycle_start: string;
    cycle_end: string;
    util_bill: CurrentCycleUtilBill | null;
    shares: CurrentCycleShare[];
}

export interface CurrentCycleUnmatchedBill {
    id: string;
    total_cost: number;
    due_date: string;
    utility_types: string[];
    util_email_id: string | null;
}

export interface CurrentCycle {
    id: string;
    month_label: string;
    bills: CurrentCycleBillRow[];
    unmatched_bills: CurrentCycleUnmatchedBill[];
}

interface AttachableExpectedBill {
    id: string;
    vendor_name: string;
    active: boolean;
}

interface CurrentBillsCardProps {
    currentCycle: CurrentCycle;
    householdId: string;
    currentUserRoommateId: string;
    expectedBills: AttachableExpectedBill[];
    billPayerVenmoHandle?: string | null;
    isCustomer?: boolean;
    currentRoommateIsAdmin?: boolean;
    /** When true, render only the inner content (no CustomCard wrapper) for embedding in another card. */
    embedded?: boolean;
    onCurrentCycleChange?: (cycle: CurrentCycle) => void;
}

interface StatusChangeTarget {
    shareId: string;
    roommateName: string;
    currentStatus: UtilBillPaymentStatus;
}

interface RejectTarget {
    utilBillId: string;
    vendorName: string;
}

interface WaiveTarget {
    billingCycleBillId: string;
    vendorName: string;
}

function formatMonthDay(dateStr: string): string {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function paymentStatusDisplay(status: UtilBillPaymentStatus): string {
    switch (status) {
        case 'paid':
            return 'Paid';
        case 'roommate_notified':
            return 'Notified';
        case 'calculated':
        default:
            return 'Pending';
    }
}

function Pill({ label, bg }: { label: string; bg: string }) {
    return (
        <Box
            sx={{
                bgcolor: bg,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.875rem',
                py: 0.5,
                px: 1.5,
                borderRadius: '9999px',
                alignSelf: 'flex-start'
            }}
        >
            {label}
        </Box>
    );
}

export default function CurrentBillsCard({
    currentCycle,
    householdId,
    currentUserRoommateId,
    expectedBills,
    // Intentionally unused: this card doesn't render a Venmo-pay button (§5 —
    // that's now the roommate digest email's job), but the prop is kept for
    // API parity with BillsCard and in case a future revision needs it here.
    billPayerVenmoHandle: _billPayerVenmoHandle,
    isCustomer = false,
    currentRoommateIsAdmin = false,
    embedded = false,
    onCurrentCycleChange
}: CurrentBillsCardProps) {
    const canManage = isCustomer || currentRoommateIsAdmin;

    const [cycleMeta, setCycleMeta] = useState({
        id: currentCycle.id,
        month_label: currentCycle.month_label
    });
    const [rows, setRows] = useState<CurrentCycleBillRow[]>(currentCycle.bills);
    const [unmatchedBills, setUnmatchedBills] = useState<CurrentCycleUnmatchedBill[]>(currentCycle.unmatched_bills);
    const [expandedRowId, setExpandedRowId] = useState<string | false>(false);

    useEffect(() => {
        setCycleMeta({
            id: currentCycle.id,
            month_label: currentCycle.month_label
        });
        setRows(currentCycle.bills);
        setUnmatchedBills(currentCycle.unmatched_bills);
    }, [currentCycle]);

    // -- Row action (approve / reject / waive / un-waive / attach) state -------
    // These all mutate server-derived state (review status, slot status,
    // matching) that this component can't fully reconstruct locally, so each
    // one refreshes the page's Inertia props on success rather than guessing
    // at the resulting row shape — the same "reload after mutation" pattern
    // BillsCard already uses for payment confirmation.
    const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
    const [actionErrors, setActionErrors] = useState<Record<string, string>>({});

    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectSubmitting, setRejectSubmitting] = useState(false);
    const [rejectError, setRejectError] = useState('');

    const [waiveDialogOpen, setWaiveDialogOpen] = useState(false);
    const [waiveTarget, setWaiveTarget] = useState<WaiveTarget | null>(null);
    const [waiveReason, setWaiveReason] = useState('');
    const [waiveSubmitting, setWaiveSubmitting] = useState(false);
    const [waiveError, setWaiveError] = useState('');

    // -- Share paid/unpaid toggle state (copied from BillsCard) -----------------
    const [statusChangeOpen, setStatusChangeOpen] = useState(false);
    const [statusChangeRowId, setStatusChangeRowId] = useState<string | null>(null);
    const [statusChangeTarget, setStatusChangeTarget] = useState<StatusChangeTarget | null>(null);
    const [updatingShareStatus, setUpdatingShareStatus] = useState(false);
    const [statusChangeError, setStatusChangeError] = useState('');

    // -- Attach-unmatched-bill state --------------------------------------------
    const [attachSelections, setAttachSelections] = useState<Record<string, string>>({});
    const [attachingId, setAttachingId] = useState<string | null>(null);
    const [attachErrors, setAttachErrors] = useState<Record<string, string>>({});

    const activeExpectedBills = expectedBills.filter((b) => b.active);

    const handleAccordionChange = (rowId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedRowId(isExpanded ? rowId : false);
    };

    const handleViewReceipt = async (utilEmailId: string) => {
        try {
            const response = await axiosClient.get(`/api/util_receipts/${utilEmailId}/presigned_url`);
            const presignedUrl = response.data.presigned_url;
            window.open(presignedUrl, '_blank');
        } catch (error) {
            console.error('Failed to get receipt URL:', error);
        }
    };

    const clearActionError = (id: string) => {
        setActionErrors((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const handleApprove = async (row: CurrentCycleBillRow) => {
        if (!row.util_bill) return;
        setActionLoadingId(row.billing_cycle_bill_id);
        clearActionError(row.billing_cycle_bill_id);
        try {
            await axiosClient.post(`/util/dashboard/${householdId}/bills/${row.util_bill.id}/approve`);
            router.reload({ onFinish: () => setActionLoadingId(null) });
        } catch (error: any) {
            setActionLoadingId(null);
            setActionErrors((prev) => ({
                ...prev,
                [row.billing_cycle_bill_id]: error?.response?.data?.error || 'Failed to approve this bill.'
            }));
        }
    };

    const handleOpenReject = (row: CurrentCycleBillRow) => {
        if (!row.util_bill) return;
        setRejectTarget({ utilBillId: row.util_bill.id, vendorName: row.vendor_name });
        setRejectReason('');
        setRejectError('');
        setRejectDialogOpen(true);
    };

    const handleCloseReject = () => {
        if (rejectSubmitting) return;
        setRejectDialogOpen(false);
        setRejectTarget(null);
        setRejectReason('');
        setRejectError('');
    };

    const handleConfirmReject = async () => {
        if (!rejectTarget) return;
        setRejectSubmitting(true);
        setRejectError('');
        try {
            await axiosClient.post(`/util/dashboard/${householdId}/bills/${rejectTarget.utilBillId}/reject`, {
                reason: rejectReason.trim() || undefined
            });
            setRejectDialogOpen(false);
            setRejectTarget(null);
            setRejectReason('');
            router.reload();
        } catch (error: any) {
            setRejectError(error?.response?.data?.error || 'Failed to reject this bill.');
        } finally {
            setRejectSubmitting(false);
        }
    };

    const handleOpenWaive = (row: CurrentCycleBillRow) => {
        setWaiveTarget({ billingCycleBillId: row.billing_cycle_bill_id, vendorName: row.vendor_name });
        setWaiveReason('');
        setWaiveError('');
        setWaiveDialogOpen(true);
    };

    const handleCloseWaive = () => {
        if (waiveSubmitting) return;
        setWaiveDialogOpen(false);
        setWaiveTarget(null);
        setWaiveReason('');
        setWaiveError('');
    };

    const handleConfirmWaive = async () => {
        if (!waiveTarget || !waiveReason.trim()) return;
        setWaiveSubmitting(true);
        setWaiveError('');
        try {
            await axiosClient.post(`/util/dashboard/${householdId}/billing_cycle_bills/${waiveTarget.billingCycleBillId}/waive`, {
                reason: waiveReason.trim()
            });
            setWaiveDialogOpen(false);
            setWaiveTarget(null);
            setWaiveReason('');
            router.reload();
        } catch (error: any) {
            setWaiveError(error?.response?.data?.error || 'Failed to waive this bill slot.');
        } finally {
            setWaiveSubmitting(false);
        }
    };

    const handleUnwaive = async (row: CurrentCycleBillRow) => {
        setActionLoadingId(row.billing_cycle_bill_id);
        clearActionError(row.billing_cycle_bill_id);
        try {
            await axiosClient.delete(`/util/dashboard/${householdId}/billing_cycle_bills/${row.billing_cycle_bill_id}/waive`);
            router.reload({ onFinish: () => setActionLoadingId(null) });
        } catch (error: any) {
            setActionLoadingId(null);
            setActionErrors((prev) => ({
                ...prev,
                [row.billing_cycle_bill_id]: error?.response?.data?.error || 'Failed to un-waive this bill slot.'
            }));
        }
    };

    const handleAttachSelectionChange = (unmatchedBillId: string, expectedBillId: string) => {
        setAttachSelections((prev) => ({ ...prev, [unmatchedBillId]: expectedBillId }));
    };

    const handleAttach = async (unmatchedBillId: string) => {
        const expectedBillId = attachSelections[unmatchedBillId];
        if (!expectedBillId) return;

        setAttachingId(unmatchedBillId);
        setAttachErrors((prev) => {
            const next = { ...prev };
            delete next[unmatchedBillId];
            return next;
        });
        try {
            await axiosClient.post(`/util/dashboard/${householdId}/bills/${unmatchedBillId}/attach`, {
                expected_bill_id: expectedBillId
            });
            router.reload({ onFinish: () => setAttachingId(null) });
        } catch (error: any) {
            setAttachingId(null);
            setAttachErrors((prev) => ({
                ...prev,
                [unmatchedBillId]: error?.response?.data?.error || 'Failed to attach this bill.'
            }));
        }
    };

    // -- Share paid/unpaid toggle logic (copied from BillsCard) -----------------

    const canCurrentUserChangeShareStatus = (share: CurrentCycleShare): boolean => {
        const isPrivileged = isCustomer || currentRoommateIsAdmin;
        if (isPrivileged) return true;

        return share.roommate_id === currentUserRoommateId && share.status === 'paid';
    };

    const handleOpenStatusChange = (rowId: string, share: CurrentCycleShare) => {
        if (!canCurrentUserChangeShareStatus(share)) return;
        setStatusChangeRowId(rowId);
        setStatusChangeTarget({
            shareId: share.id,
            roommateName: share.roommate_name,
            currentStatus: share.status
        });
        setStatusChangeError('');
        setStatusChangeOpen(true);
    };

    const handleCloseStatusChange = () => {
        if (updatingShareStatus) return;
        setStatusChangeOpen(false);
        setStatusChangeRowId(null);
        setStatusChangeTarget(null);
        setStatusChangeError('');
    };

    const handleConfirmStatusChange = async () => {
        if (!householdId || !statusChangeTarget || !statusChangeRowId) return;

        const nextStatus: 'paid' | 'roommate_notified' = statusChangeTarget.currentStatus === 'paid'
            ? 'roommate_notified'
            : 'paid';

        setUpdatingShareStatus(true);
        setStatusChangeError('');
        try {
            await axiosClient.patch(`/util/dashboard/${householdId}/bill-shares/status`, {
                util_bill_share_id: statusChangeTarget.shareId,
                status: nextStatus
            });

            setRows((prev) => prev.map((row) => {
                if (row.billing_cycle_bill_id !== statusChangeRowId) return row;
                return {
                    ...row,
                    shares: row.shares.map((share) => (
                        share.id === statusChangeTarget.shareId ? { ...share, status: nextStatus } : share
                    ))
                };
            }));

            setStatusChangeOpen(false);
            setStatusChangeRowId(null);
            setStatusChangeTarget(null);
        } catch (error: any) {
            setStatusChangeError(error?.response?.data?.error || 'Failed to update payment status.');
        } finally {
            setUpdatingShareStatus(false);
        }
    };

    if (rows.length === 0 && unmatchedBills.length === 0) return null;

    const content = (
        <>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 2 }}>
                <Typography fontWeight="bold" variant="h6">
                    {cycleMeta.month_label} bills
                </Typography>
            </Box>
            {rows.map((row) => {
                const expanded = expandedRowId === row.billing_cycle_bill_id;
                const isLoadingRow = actionLoadingId === row.billing_cycle_bill_id;
                const rowError = actionErrors[row.billing_cycle_bill_id];

                return (
                    <Accordion
                        key={row.billing_cycle_bill_id}
                        expanded={expanded}
                        onChange={handleAccordionChange(row.billing_cycle_bill_id)}
                        sx={{
                            boxShadow: 'none',
                            borderRadius: '8px !important',
                            mb: 1.5,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': { margin: '0 0 12px 0' }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: 'grey.700' }} />}
                            sx={{
                                px: 2,
                                py: 0,
                                minHeight: 56,
                                '& .MuiAccordionSummary-content': { my: 1.5 },
                                '&.Mui-expanded': { minHeight: 56, borderBottom: '1px solid', borderColor: 'grey.200' }
                            }}
                        >
                            <Box display="flex" flexDirection="column" gap={0.75} sx={{ width: '100%' }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2" fontWeight="bold">{row.vendor_name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatUtilityTypes(row.utility_types)}
                                    </Typography>
                                </Box>
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                    {row.status === 'expected' && (
                                        <>
                                            <Pill label="Expected" bg={EXPECTED_GREY} />
                                            <Typography variant="body2" color="text.secondary">
                                                Cycle {formatMonthDay(row.cycle_start)} &ndash; {formatMonthDay(row.cycle_end)}
                                                {' '}&middot; Due day {row.due_day}
                                            </Typography>
                                        </>
                                    )}
                                    {row.status === 'waived' && <Pill label="Waived" bg={WAIVED_GREY} />}
                                    {row.status === 'pending_review' && row.util_bill && (
                                        <>
                                            <Pill label="Pending Review" bg={PENDING_REVIEW_AMBER} />
                                            <Typography variant="body2" fontWeight="bold">
                                                ${row.util_bill.total_cost.toFixed(2)}
                                            </Typography>
                                        </>
                                    )}
                                    {row.status === 'approved' && row.util_bill && (
                                        <Typography variant="body2" fontWeight="bold">
                                            ${row.util_bill.total_cost.toFixed(2)}
                                        </Typography>
                                    )}
                                    {row.status === 'auto_approved' && row.util_bill && (
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" fontWeight="bold">
                                                ${row.util_bill.total_cost.toFixed(2)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                Not reviewed
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                {canManage && (
                                    <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()} sx={{ mt: 0.5 }}>
                                        {row.status === 'pending_review' && row.util_bill && (
                                            <>
                                                <SecondaryButton
                                                    size="small"
                                                    disabled={isLoadingRow}
                                                    onClick={() => handleApprove(row)}
                                                >
                                                    {isLoadingRow ? <CircularProgress size={16} /> : 'Approve'}
                                                </SecondaryButton>
                                                <SecondaryButton
                                                    size="small"
                                                    disabled={isLoadingRow}
                                                    onClick={() => handleOpenReject(row)}
                                                >
                                                    Reject
                                                </SecondaryButton>
                                            </>
                                        )}
                                        {row.status === 'expected' && (
                                            <SecondaryButton
                                                size="small"
                                                disabled={isLoadingRow}
                                                onClick={() => handleOpenWaive(row)}
                                            >
                                                Waive
                                            </SecondaryButton>
                                        )}
                                        {row.status === 'waived' && (
                                            <SecondaryButton
                                                size="small"
                                                disabled={isLoadingRow}
                                                onClick={() => handleUnwaive(row)}
                                            >
                                                {isLoadingRow ? <CircularProgress size={16} /> : 'Un-waive'}
                                            </SecondaryButton>
                                        )}
                                    </Box>
                                )}
                                {rowError && (
                                    <Typography variant="caption" color="error">
                                        {rowError}
                                    </Typography>
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, py: 2, pt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Roommates
                            </Typography>
                            {row.shares.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No shares yet.
                                </Typography>
                            )}
                            {row.shares.map((share) => (
                                <Box
                                    key={share.id}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'grey.100' }}
                                >
                                    <Typography variant="body2">{share.roommate_name}</Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" fontWeight="bold">
                                            ${share.roommate_cost.toFixed(2)}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: share.status === 'paid' ? FOREST_GREEN : 'text.secondary',
                                                fontWeight: share.status === 'paid' ? 600 : 400
                                            }}
                                        >
                                            {paymentStatusDisplay(share.status)}
                                        </Typography>
                                        {canCurrentUserChangeShareStatus(share) && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenStatusChange(row.billing_cycle_bill_id, share)}
                                                aria-label="Change payment status"
                                                sx={{ p: 0.25, color: 'text.secondary' }}
                                            >
                                                <SettingsIcon fontSize="inherit" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                            {row.util_bill?.util_email_id && (
                                <SecondaryButton
                                    fullWidth
                                    onClick={() => handleViewReceipt(row.util_bill!.util_email_id!)}
                                    sx={{ mt: 2 }}
                                >
                                    View Receipt
                                </SecondaryButton>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {unmatchedBills.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography fontWeight="bold" variant="subtitle1" sx={{ mb: 1 }}>
                        Unmatched bills
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        These bills arrived but didn&apos;t match any roster line.
                    </Typography>
                    {unmatchedBills.map((bill) => {
                        const selected = attachSelections[bill.id] ?? '';
                        const attaching = attachingId === bill.id;
                        const attachError = attachErrors[bill.id];
                        return (
                            <Box
                                key={bill.id}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: '8px',
                                    p: 1.5,
                                    mb: 1.5
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="body2" fontWeight="bold">
                                        ${bill.total_cost.toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Due {formatMonthDay(bill.due_date)}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {formatUtilityTypes(bill.utility_types)}
                                </Typography>
                                {canManage && (
                                    <Box display="flex" gap={1} alignItems="center">
                                        <FormControl size="small" fullWidth>
                                            <InputLabel id={`attach-select-${bill.id}`}>Attach to roster line</InputLabel>
                                            <Select
                                                labelId={`attach-select-${bill.id}`}
                                                label="Attach to roster line"
                                                value={selected}
                                                onChange={(e) => handleAttachSelectionChange(bill.id, e.target.value as string)}
                                                disabled={attaching}
                                            >
                                                {activeExpectedBills.map((eb) => (
                                                    <MenuItem key={eb.id} value={eb.id}>
                                                        {eb.vendor_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <SecondaryButton
                                            disabled={!selected || attaching}
                                            onClick={() => handleAttach(bill.id)}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            {attaching ? <CircularProgress size={16} /> : 'Attach'}
                                        </SecondaryButton>
                                    </Box>
                                )}
                                {attachError && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                        {attachError}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </>
    );

    const dialogs = (
        <>
            <Dialog
                open={rejectDialogOpen}
                onClose={handleCloseReject}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Reject {rejectTarget?.vendorName} bill
                    </Typography>
                    <IconButton onClick={handleCloseReject} size="small" disabled={rejectSubmitting}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This voids the bill and marks its roster slot as waived for this cycle, since a rejected bill
                        usually means nothing legitimate is coming. You can un-waive the slot afterward if you're
                        still expecting a corrected bill.
                    </Typography>
                    {rejectError && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {rejectError}
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        label="Reason (optional)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        multiline
                        minRows={2}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                        <SecondaryButton fullWidth onClick={handleCloseReject} disabled={rejectSubmitting}>
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton fullWidth onClick={handleConfirmReject} disabled={rejectSubmitting}>
                            {rejectSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Reject'}
                        </PrimaryButton>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={waiveDialogOpen}
                onClose={handleCloseWaive}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Waive {waiveTarget?.vendorName} for this cycle
                    </Typography>
                    <IconButton onClick={handleCloseWaive} size="small" disabled={waiveSubmitting}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This stops the cycle from waiting on a bill for this vendor. A reason is required.
                    </Typography>
                    {waiveError && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {waiveError}
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        label="Reason"
                        value={waiveReason}
                        onChange={(e) => setWaiveReason(e.target.value)}
                        required
                        multiline
                        minRows={2}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                        <SecondaryButton fullWidth onClick={handleCloseWaive} disabled={waiveSubmitting}>
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton fullWidth onClick={handleConfirmWaive} disabled={waiveSubmitting || !waiveReason.trim()}>
                            {waiveSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Waive'}
                        </PrimaryButton>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={statusChangeOpen}
                onClose={handleCloseStatusChange}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {statusChangeTarget?.currentStatus === 'paid' ? 'Mark as pending?' : 'Mark as paid?'}
                    </Typography>
                    <IconButton onClick={handleCloseStatusChange} size="small" disabled={updatingShareStatus}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        You are updating {statusChangeTarget?.roommateName}&apos;s payment status.
                    </Typography>
                    {statusChangeError && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {statusChangeError}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <SecondaryButton fullWidth onClick={handleCloseStatusChange} disabled={updatingShareStatus}>
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton
                            fullWidth
                            onClick={handleConfirmStatusChange}
                            disabled={updatingShareStatus || !statusChangeTarget}
                        >
                            {updatingShareStatus ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
                        </PrimaryButton>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );

    if (embedded) {
        return (
            <>
                <Box sx={{ mb: 2 }}>{content}</Box>
                {dialogs}
            </>
        );
    }

    return (
        <>
            <CustomCard>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    {content}
                </CardContent>
            </CustomCard>
            {dialogs}
        </>
    );
}
